// ============================================
// Resort Cocktail Minigame — "Read the Room"
// Tiki Bar shift minigame triggered via #ui:resort_cocktail.
// Pick a cocktail category that matches each guest's vibe.
// Perfect match = full tip. Acceptable = partial. Wrong = flat consolation.
// 3 misses → Iris ends the shift early.
// Writes resort_cocktail_tip + resort_cocktail_grade when done.
// ============================================

import { events, Events } from '../engine/events.js';
import { storyManager } from '../engine/story.js';
import { L } from '../systems/localization.js';

const UI_TRIGGER = 'resort_cocktail';

// Static data with EN labels — UI surfaces wrap with L.get('resort_cocktail.X.label')
// at render time so display text changes per language.
const CATEGORIES = [
    { id: 'classic',  label: 'Classic',  iconFile: 'icon_cocktail_classic' },
    { id: 'fruity',   label: 'Fruity',   iconFile: 'icon_cocktail_fruity' },
    { id: 'spicy',    label: 'Spicy',    iconFile: 'icon_cocktail_spicy' },
    { id: 'bitter',   label: 'Bitter',   iconFile: 'icon_cocktail_bitter' },
    { id: 'tropical', label: 'Tropical', iconFile: 'icon_cocktail_tropical' },
    { id: 'mocktail', label: 'Mocktail', iconFile: 'icon_cocktail_mocktail' },
];
const _catLabel = (c) => L.get(`resort_cocktail.cat.${c.id}.label`, c.label);

// Guest templates: mood line, primary category, optional secondary, Fem-30 hint
const GUEST_TEMPLATES = [
    { id: 'g_first_date',        primary: 'classic',  secondary: null,       moodFb: 'classy first date',         hintFb: 'dressed up, studying the menu twice' },
    { id: 'g_bachelorette',      primary: 'fruity',   secondary: null,       moodFb: 'bachelorette tipsy',        hintFb: 'sashes, sugar-forward order energy' },
    { id: 'g_sunburned',         primary: 'tropical', secondary: null,       moodFb: 'sunburned tourists',        hintFb: 'sand in the shoes, asking about coconut' },
    { id: 'g_business',          primary: 'bitter',   secondary: null,       moodFb: 'business meeting',          hintFb: 'two phones, zero small talk' },
    { id: 'g_post_workout',      primary: 'mocktail', secondary: null,       moodFb: 'post-workout fit',          hintFb: 'towel round the neck, water-bottle reflex' },
    { id: 'g_honeymoon',         primary: 'classic',  secondary: 'fruity',   moodFb: 'honeymoon quiet',           hintFb: 'hand on hand, whispering' },
    { id: 'g_rowdy_boys',        primary: 'spicy',    secondary: null,       moodFb: 'rowdy boys group',          hintFb: 'loud, dare energy, eyeing the wall of hot sauce' },
    { id: 'g_anniversary',       primary: 'bitter',   secondary: 'classic',  moodFb: 'older couple anniversary',  hintFb: 'silver hair, reads the wine list first' },
    { id: 'g_influencer',        primary: 'tropical', secondary: 'fruity',   moodFb: 'influencer photo op',       hintFb: 'ring light, umbrella-and-pineapple required' },
    { id: 'g_solo_traveller',    primary: 'classic',  secondary: 'mocktail', moodFb: 'first-time solo traveller', hintFb: 'careful with the menu, polite to everyone' },
];
const _guestMood = (g) => L.get(`resort_cocktail.guest.${g.id}.mood`, g.moodFb);
const _guestHint = (g) => L.get(`resort_cocktail.guest.${g.id}.hint`, g.hintFb);

const TIP_PERFECT    = 12;
const TIP_ACCEPTABLE = 8;
const TIP_WRONG      = 3;
const MAX_MISTAKES   = 3;
const SHIFT_DURATION = 180000; // 3 min
const GUEST_PATIENCE = 30000;  // 30s per guest
const QUEUE_SIZE     = 3;

const PERF_TIERS = [
    { min: 0,   grade: 'C',  labelFb: 'Read it poorly',  labelKey: 'resort_cocktail.perf.poor.label',    gradeN: 0 },
    { min: 30,  grade: 'B',  labelFb: 'Solid read',      labelKey: 'resort_cocktail.perf.solid.label',   gradeN: 1 },
    { min: 60,  grade: 'A',  labelFb: 'Great read',      labelKey: 'resort_cocktail.perf.great.label',   gradeN: 2 },
    { min: 90,  grade: 'A+', labelFb: 'Perfect read',    labelKey: 'resort_cocktail.perf.perfect.label', gradeN: 3 },
];
const _perfLabel = (t) => L.get(t.labelKey, t.labelFb);

class ResortCocktailMinigame {
    constructor() {
        this._root = null;
        this._isActive = false;
        this._queue = [];
        this._nextGuestId = 0;
        this._totalTip = 0;
        this._mistakes = 0;
        this._perfectCount = 0;
        this._servedCount = 0;
        this._selectedGuestId = null;
        this._shiftStart = 0;
        this._shiftInterval = null;
        this._patienceInterval = null;
        this._finished = false;
        this._femShowsHints = false;
        this._storyPrevDisplay = '';

        this._scope = events.createScope('resort-cocktail-minigame');
        this._injectStyles();
        this._bindEvents();
    }

    isActive() { return this._isActive; }

    destroy() {
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
        this._stopTimers();
    }

    _bindEvents() {
        this._scope.on(Events.TAG_UI, (mode) => {
            if (mode === UI_TRIGGER) {
                this._activate();
            } else if (this._isActive) {
                this._deactivate(false);
            }
        });
    }

    _activate() {
        this._stopTimers();
        this._isActive = true;
        this._queue = [];
        this._nextGuestId = 0;
        this._totalTip = 0;
        this._mistakes = 0;
        this._perfectCount = 0;
        this._servedCount = 0;
        this._selectedGuestId = null;
        this._finished = false;

        try {
            const story = storyManager.currentStory;
            if (story) {
                const fem = story.variablesState['Femininity'] || 0;
                this._femShowsHints = fem >= 30;
            }
        } catch(e) { /* default */ }

        storyManager.waitForInput();
        if (!this._root) this._createContainer();

        const story = document.getElementById('story');
        if (story) {
            this._storyPrevDisplay = story.style.display || '';
            story.style.display = 'none';
        }

        this._buildUI();
        this._fillQueue();
        this._startShift();
        this._root.classList.remove('hidden');
    }

    _deactivate(resume = true) {
        this._isActive = false;
        this._stopTimers();
        if (this._root) this._root.classList.add('hidden');
        const story = document.getElementById('story');
        if (story) story.style.display = this._storyPrevDisplay || '';
        if (resume) storyManager.releaseInput();
    }

    _createContainer() {
        const el = document.createElement('div');
        el.id = 'resort-cocktail-panel';
        el.className = 'bar-mg-panel resort-cocktail-panel hidden';
        const story = document.getElementById('story');
        if (story) {
            story.parentNode.insertBefore(el, story);
        } else {
            document.body.appendChild(el);
        }
        this._root = el;
    }

    _buildUI() {
        this._root.innerHTML = '';

        const header = document.createElement('div');
        header.className = 'bar-mg-header';
        header.innerHTML = `
            <div class="bar-mg-title"><img class="bar-mg-title-icon" src="assets/images/UI/icon_cocktail_mix.png" alt="" aria-hidden="true">Read the Room</div>
            <div class="bar-mg-recipe">Tap a guest, then match their vibe to a category</div>
        `;
        this._root.appendChild(header);

        const timerBar = document.createElement('div');
        timerBar.className = 'bar-mg-timer-track';
        timerBar.innerHTML = '<div class="bar-mg-timer-fill" id="rc-timer"></div><span class="bar-mg-timer-text" id="rc-timer-text"></span>';
        this._root.appendChild(timerBar);

        const stats = document.createElement('div');
        stats.className = 'rc-stats';
        stats.innerHTML = `
            <span class="rc-tip">Tips: <strong id="rc-tip">$0</strong></span>
            <span class="rc-served">Served: <strong id="rc-served">0</strong></span>
            <span class="rc-mistakes">Misses: <strong id="rc-mistakes">0</strong>/${MAX_MISTAKES}</span>
        `;
        this._root.appendChild(stats);

        const fb = document.createElement('div');
        fb.className = 'bar-mg-feedback';
        fb.id = 'rc-feedback';
        fb.textContent = 'New shift. Tap a guest to take their order.';
        this._root.appendChild(fb);

        const queue = document.createElement('div');
        queue.className = 'rc-queue';
        queue.id = 'rc-queue';
        this._root.appendChild(queue);

        const cats = document.createElement('div');
        cats.className = 'rc-categories';
        for (const cat of CATEGORIES) {
            const btn = document.createElement('button');
            btn.className = 'rc-cat-btn';
            btn.dataset.cat = cat.id;
            btn.innerHTML = `<img class="rc-cat-icon" src="assets/images/UI/${cat.iconFile}.png" alt="" aria-hidden="true"><span class="rc-cat-label">${_catLabel(cat)}</span>`;
            btn.disabled = true;
            btn.addEventListener('click', () => this._onCategoryClick(cat.id));
            cats.appendChild(btn);
        }
        this._root.appendChild(cats);

        const skip = document.createElement('button');
        skip.className = 'bar-mg-skip';
        skip.textContent = 'End shift';
        skip.addEventListener('click', () => this._onSkip());
        this._root.appendChild(skip);
    }

    _fillQueue() {
        while (this._queue.length < QUEUE_SIZE) {
            const tmpl = GUEST_TEMPLATES[Math.floor(Math.random() * GUEST_TEMPLATES.length)];
            const id = `g${this._nextGuestId++}`;
            this._queue.push({
                id,
                tmplId: tmpl.id,
                mood: _guestMood(tmpl),
                primary: tmpl.primary,
                secondary: tmpl.secondary,
                hint: _guestHint(tmpl),
                patienceStart: Date.now(),
                patienceMs: GUEST_PATIENCE,
            });
        }
        this._renderQueue();
    }

    _renderQueue() {
        const el = document.getElementById('rc-queue');
        if (!el) return;
        el.innerHTML = '';
        for (const g of this._queue) {
            const div = document.createElement('button');
            div.className = 'rc-guest' + (this._selectedGuestId === g.id ? ' rc-guest-active' : '');
            div.dataset.gid = g.id;
            const patience = Math.max(0, 1 - (Date.now() - g.patienceStart) / g.patienceMs);
            const hintLine = this._femShowsHints
                ? `<span class="rc-hint">${g.hint}</span>`
                : '';
            div.innerHTML = `
                <span class="rc-guest-mood">${g.mood}</span>
                ${hintLine}
                <div class="rc-patience"><div class="rc-patience-fill" style="width: ${patience * 100}%"></div></div>
            `;
            div.addEventListener('click', () => this._onGuestClick(g.id));
            el.appendChild(div);
        }
    }

    _onGuestClick(gid) {
        if (this._finished) return;
        this._selectedGuestId = gid;
        this._renderQueue();
        this._updateCategoryState();
        const fb = document.getElementById('rc-feedback');
        const g = this._queue.find(x => x.id === gid);
        if (fb && g) {
            fb.textContent = L.get('resort_cocktail.pick_category', '{0} — pick a category').replace('{0}', g.mood);
            fb.className = 'bar-mg-feedback';
        }
    }

    _updateCategoryState() {
        const enabled = !!this._selectedGuestId && !this._finished;
        this._root.querySelectorAll('.rc-cat-btn').forEach(b => {
            b.disabled = !enabled;
        });
    }

    _onCategoryClick(catId) {
        if (this._finished || !this._selectedGuestId) return;
        const gi = this._queue.findIndex(g => g.id === this._selectedGuestId);
        if (gi === -1) return;
        const g = this._queue[gi];

        let tip;
        let quality;
        if (catId === g.primary) {
            tip = TIP_PERFECT;
            quality = 'perfect';
            this._perfectCount++;
        } else if (g.secondary && catId === g.secondary) {
            tip = TIP_ACCEPTABLE;
            quality = 'ok';
        } else {
            tip = TIP_WRONG;
            quality = 'wrong';
            this._mistakes++;
        }

        this._totalTip += tip;
        this._servedCount++;
        this._queue.splice(gi, 1);
        this._selectedGuestId = null;

        const fb = document.getElementById('rc-feedback');
        if (fb) {
            if (quality === 'perfect') {
                fb.textContent = `Perfect match. +$${tip}`;
                fb.className = 'bar-mg-feedback bar-mg-fb-good';
            } else if (quality === 'ok') {
                fb.textContent = `Acceptable. +$${tip}`;
                fb.className = 'bar-mg-feedback';
            } else {
                fb.textContent = `Missed the vibe. +$${tip}`;
                fb.className = 'bar-mg-feedback bar-mg-fb-bad';
            }
        }

        this._updateStats();

        if (this._mistakes >= MAX_MISTAKES) {
            this._finish('shift_cut');
            return;
        }

        this._fillQueue();
        this._updateCategoryState();
    }

    _updateStats() {
        const t = document.getElementById('rc-tip');
        const s = document.getElementById('rc-served');
        const m = document.getElementById('rc-mistakes');
        if (t) t.textContent = `$${this._totalTip}`;
        if (s) s.textContent = this._servedCount;
        if (m) m.textContent = this._mistakes;
    }

    _onSkip() {
        if (this._finished) return;
        this._finish('skip');
    }

    _startShift() {
        this._shiftStart = Date.now();
        this._shiftInterval = setInterval(() => {
            if (this._finished) return;
            const elapsed = Date.now() - this._shiftStart;
            const pct = Math.max(0, 1 - elapsed / SHIFT_DURATION);
            const bar = document.getElementById('rc-timer');
            if (bar) bar.style.width = `${pct * 100}%`;
            const txt = document.getElementById('rc-timer-text');
            if (txt) {
                const left = Math.max(0, Math.ceil((SHIFT_DURATION - elapsed) / 1000));
                const mm = Math.floor(left / 60);
                const ss = left % 60;
                txt.textContent = `${mm}:${ss.toString().padStart(2, '0')}`;
            }
            if (elapsed >= SHIFT_DURATION) {
                this._finish('timeout');
            }
        }, 100);

        this._patienceInterval = setInterval(() => {
            if (this._finished) return;
            const now = Date.now();
            let anyTimedOut = false;
            for (let i = this._queue.length - 1; i >= 0; i--) {
                const g = this._queue[i];
                if (now - g.patienceStart >= g.patienceMs) {
                    this._queue.splice(i, 1);
                    this._mistakes++;
                    this._servedCount++;
                    anyTimedOut = true;
                }
            }
            if (this._selectedGuestId && !this._queue.find(g => g.id === this._selectedGuestId)) {
                this._selectedGuestId = null;
                this._updateCategoryState();
            }
            if (this._mistakes >= MAX_MISTAKES) {
                this._updateStats();
                this._finish('shift_cut');
                return;
            }
            if (anyTimedOut) {
                const fb = document.getElementById('rc-feedback');
                if (fb) {
                    fb.textContent = 'A guest walked. Patience runs out.';
                    fb.className = 'bar-mg-feedback bar-mg-fb-bad';
                }
                this._updateStats();
            }
            this._fillQueue();
            // Re-render queue every tick so patience bars drain visually
            this._renderQueue();
        }, 500);
    }

    _stopTimers() {
        if (this._shiftInterval) {
            clearInterval(this._shiftInterval);
            this._shiftInterval = null;
        }
        if (this._patienceInterval) {
            clearInterval(this._patienceInterval);
            this._patienceInterval = null;
        }
    }

    _finish(reason) {
        if (this._finished) return;
        this._finished = true;
        this._stopTimers();

        // Performance score 0-100: perfect rate (0-60) + volume (0-30) − mistake penalty
        const perfectPct = this._servedCount > 0 ? this._perfectCount / this._servedCount : 0;
        const volumeScore = Math.min(30, this._servedCount * 3);
        const mistakePenalty = this._mistakes * 10;
        const score = Math.max(0, Math.round(perfectPct * 60 + volumeScore - mistakePenalty));

        // Skimpy-uniform +50% (Iris Stage 2 unlock)
        let uniformBonus = 0;
        try {
            const story = storyManager.currentStory;
            if (story && story.variablesState['has_skimpy_staff_uniform']) {
                uniformBonus = Math.round(this._totalTip * 0.5);
            }
        } catch(e) { /* ignore */ }

        const finalTip = this._totalTip + uniformBonus;

        let tier = PERF_TIERS[0];
        for (let i = PERF_TIERS.length - 1; i >= 0; i--) {
            if (score >= PERF_TIERS[i].min) { tier = PERF_TIERS[i]; break; }
        }

        const fb = document.getElementById('rc-feedback');
        if (fb) {
            let msg = L.get('resort_cocktail.summary.line', '{0} · {1}. Total tips: ${2}')
                .replace('{0}', tier.grade)
                .replace('{1}', _perfLabel(tier))
                .replace('{2}', String(finalTip));
            if (uniformBonus > 0) msg += ' ' + L.get('resort_cocktail.summary.uniform_bonus', '(incl. ${0} uniform bonus)').replace('{0}', String(uniformBonus));
            if (reason === 'shift_cut') msg = L.get('resort_cocktail.summary.shift_cut', 'Iris pulls you off the floor after three misses.') + ' ' + msg;
            else if (reason === 'skip') msg = L.get('resort_cocktail.summary.skip', 'You duck out before the rush peaks.') + ' ' + msg;
            fb.textContent = msg;
            fb.className = `bar-mg-feedback ${tier.gradeN >= 2 ? 'bar-mg-fb-good' : 'bar-mg-fb-bad'}`;
        }

        this._root.querySelectorAll('button:not(.bar-mg-skip)').forEach(b => {
            b.disabled = true;
        });

        this._writeResults(finalTip, tier.gradeN);
        setTimeout(() => this._deactivate(true), 3500);
    }

    _writeResults(tip, grade) {
        try {
            const story = storyManager.currentStory;
            if (story) {
                story.variablesState['resort_cocktail_tip'] = tip;
                story.variablesState['resort_cocktail_grade'] = grade;
            }
        } catch(e) {
            console.warn('[ResortCocktailMinigame] Could not write results:', e);
        }
    }

    _injectStyles() {
        if (document.getElementById('resort-cocktail-styles')) return;
        const style = document.createElement('style');
        style.id = 'resort-cocktail-styles';
        style.textContent = `
/* Resort Cocktail Minigame — reuses .bar-mg-panel base with resort-tinted palette */

.resort-cocktail-panel {
    max-width: 720px;
    background: linear-gradient(rgba(8, 20, 26, 0.95), rgba(14, 28, 35, 0.95));
    border: 1px solid rgba(100, 220, 200, 0.18);
}

.resort-cocktail-panel .bar-mg-title {
    color: #f0d68e;
}

.rc-stats {
    display: flex;
    justify-content: space-between;
    padding: 6px 14px;
    background: rgba(12, 20, 22, 0.88);
    border-radius: 8px;
    font-size: 13px;
    gap: 10px;
}

.rc-tip strong      { color: #5ad6a0; font-size: 15px; }
.rc-served strong   { color: #b0d4e8; font-size: 15px; }
.rc-mistakes strong { color: #ff9070; font-size: 15px; }

.rc-queue {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 8px;
    padding: 4px 0;
}

.rc-guest {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 12px;
    background: rgba(30, 45, 55, 0.7);
    border: 1px solid rgba(100, 180, 180, 0.2);
    border-radius: 8px;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    color: var(--text-body, #e0e0e0);
    transition: border-color 120ms, background 120ms;
}

.rc-guest:hover:not(:disabled) {
    background: rgba(40, 60, 75, 0.8);
    border-color: rgba(140, 220, 210, 0.4);
}

.rc-guest.rc-guest-active {
    background: rgba(50, 90, 100, 0.9);
    border-color: #7ad6c8;
    box-shadow: 0 0 12px rgba(120, 220, 210, 0.3);
}

.rc-guest-mood {
    font-size: 14px;
    font-weight: 500;
    color: #f0e6d0;
}

.rc-hint {
    font-size: 11px;
    color: #88c0b8;
    font-style: italic;
    opacity: 0.85;
}

.rc-patience {
    height: 6px;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 3px;
    overflow: hidden;
}

.rc-patience-fill {
    height: 100%;
    background: linear-gradient(90deg, #5ad6a0, #f0d68e, #ff9070);
    transition: width 120ms linear;
}

.rc-categories {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    padding: 4px 0;
}

.rc-cat-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 8px;
    background: rgba(40, 60, 70, 0.65);
    border: 1px solid rgba(100, 180, 180, 0.18);
    border-radius: 8px;
    color: #e8e0c0;
    font-size: 13px;
    font-family: inherit;
    cursor: pointer;
    transition: background 120ms, border-color 120ms;
}

.rc-cat-btn:hover:not(:disabled) {
    background: rgba(70, 110, 120, 0.9);
    border-color: rgba(140, 220, 210, 0.4);
}

.rc-cat-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.rc-cat-icon {
    width: 22px;
    height: 22px;
    object-fit: contain;
    flex-shrink: 0;
    image-rendering: -webkit-optimize-contrast;
}

.bar-mg-title-icon {
    width: 22px;
    height: 22px;
    object-fit: contain;
    vertical-align: -5px;
    margin-right: 6px;
}

@media (max-width: 500px) {
    .rc-categories { grid-template-columns: repeat(2, 1fr); }
    .rc-queue { grid-template-columns: 1fr; }
    .rc-stats { flex-wrap: wrap; }
}
        `;
        document.head.appendChild(style);
    }
}

export const resortCocktailMinigame = new ResortCocktailMinigame();
